/**
 * Peg Top Loader — https://uiverse.io/andrew-manzyk/fast-vampirebat-53
 *
 * MIT License
 * Copyright - 2026 andrew-manzyk (Andrew Manzyk)
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { useId } from 'react'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import './PegTopLoader.css'

const PEG_PATH = 'M63,37c-6.7-4-4-27-13-27s-6.3,23-13,27-27,4-27,13,20.3,9,27,13,4,27,13,27,6.3-23,13-27,27-4,27-13-20.3-9-27-13Z'

function PegTopGlyph({ prefix }) {
  const shine = `${prefix}-shine`
  const mask = `${prefix}-mask`
  const g1 = `${prefix}-g1`
  const g2 = `${prefix}-g2`
  const g3 = `${prefix}-g3`
  const g4 = `${prefix}-g4`
  const g5 = `${prefix}-g5`
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" aria-hidden="true">
      <defs>
        <filter id={shine}>
          <feGaussianBlur stdDeviation="3" />
        </filter>
        <mask id={mask}>
          <path d={PEG_PATH} fill="white" />
        </mask>
        <radialGradient
          id={g1}
          cx="50"
          cy="66"
          fx="50"
          fy="66"
          r="30"
          gradientTransform="translate(0 35) scale(1 0.5)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="black" stopOpacity="0.3" />
          <stop offset="50%" stopColor="black" stopOpacity="0.1" />
          <stop offset="100%" stopColor="black" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={g2} cx="55" cy="20" fx="55" fy="20" r="30" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="white" stopOpacity="0.3" />
          <stop offset="50%" stopColor="white" stopOpacity="0.1" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={g3} cx="85" cy="50" fx="85" fy="50" href={`#${g2}`} />
        <radialGradient
          id={g4}
          cx="50"
          cy="58"
          fx="50"
          fy="58"
          r="60"
          gradientTransform="translate(0 47) scale(1 0.2)"
          href={`#${g3}`}
        />
        <linearGradient id={g5} x1="50" y1="90" x2="50" y2="10" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="black" stopOpacity="0.2" />
          <stop offset="40%" stopColor="black" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g>
        <path d={PEG_PATH} fill="currentColor" />
        <path d={PEG_PATH} fill={`url(#${g1})`} />
        <path
          d={PEG_PATH}
          fill="none"
          stroke="white"
          opacity="0.3"
          strokeWidth="3"
          filter={`url(#${shine})`}
          mask={`url(#${mask})`}
        />
        <path d={PEG_PATH} fill={`url(#${g2})`} />
        <path d={PEG_PATH} fill={`url(#${g3})`} />
        <path d={PEG_PATH} fill={`url(#${g4})`} />
        <path d={PEG_PATH} fill={`url(#${g5})`} />
      </g>
    </svg>
  )
}

export default function PegTopLoader({
  label = 'Chargement des produits…',
  compact = false,
}) {
  const uid = useId().replace(/:/g, '')
  const reducedMotion = usePrefersReducedMotion()

  return (
    <div
      className={`peg-top-loader${compact ? ' is-compact' : ''}${reducedMotion ? ' is-reduced' : ''}`}
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="peg-top-loader__stage" aria-hidden="true">
        <span className="peg-top-loader__peg peg-top-loader__peg--one">
          <PegTopGlyph prefix={`${uid}-one`} />
        </span>
        {reducedMotion ? null : (
          <>
            <span className="peg-top-loader__peg peg-top-loader__peg--two">
              <PegTopGlyph prefix={`${uid}-two`} />
            </span>
            <span className="peg-top-loader__peg peg-top-loader__peg--three">
              <PegTopGlyph prefix={`${uid}-three`} />
            </span>
          </>
        )}
      </div>
      {label ? <p className="peg-top-loader__label">{label}</p> : null}
    </div>
  )
}
